import { Project } from '@/app/models/project';
import { useCopyExpenseExceptionMutation } from '@/app/services/expenseExceptionApi';
import { useProjectsQuery } from '@/app/services/projectApi';
import searchIcon from '@/icons/search.svg';
import { Autocomplete, Box, InputAdornment, InputProps, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CancelOrConfirmDialog from '../common/dialogs/CancelOrConfirmDialog';

type CopyExceptionDrawerProps = {
  onClose: () => void;
  copyProject: {
    code: string;
    destiniCode: string;
    exceptions: number[];
  };
};

export default function CopyExceptionDrawer(props: CopyExceptionDrawerProps) {
  const { onClose, copyProject } = props;

  const [copyExpenseException] = useCopyExpenseExceptionMutation();

  const { t } = useTranslation(['exceptions', 'common']);

  const [costCenter, setCostCenter] = useState('');
  const [searchCostCenter, setSearchCostCenter] = useState('');

  const [payingCostCenter, setPayingCostCenter] = useState('');
  const [searchPayingCostCenter, setSearchPayingCostCenter] = useState('');
  const [selectedPayingCostCenter, setSelectedPayingCostCenter] = useState(null as unknown as Project);

  const [confirmDialog, setConfirmDialog] = useState(false);

  const [selectedCostCenter, setSelectedCostCenter] = useState(null as unknown as Project);

  const { data: costCenters } = useProjectsQuery({
    bringClose: false,
    searchTerm: searchCostCenter,
    page: 0,
    size: 10,
  });
  const { data: payingCostCenters } = useProjectsQuery({
    bringClose: false,
    searchTerm: searchPayingCostCenter,
    page: 0,
    size: 10,
  });

  const handleSubmit = async () => {
    const response = await copyExpenseException({
      projectCode: copyProject.code,
      destiniCode: copyProject.destiniCode,
      newProjectCode: selectedCostCenter.code,
      newDestiniCode: selectedPayingCostCenter.code,
    });
    if ('data' in response) {
      toast.success(
        t('exception.case.copied.successfully', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh )',
        maxHeight: 'calc(100vh )',
        flexGrow: 'inherit',
        background: '#FAFAFA',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 'inherit' }}>
        <div style={{ color: '#4B4B4B', fontSize: '18px', fontWeight: 'bold', marginBottom: '24px' }}>
          <Typography variant="h1">{t('copy.exception.cases')}</Typography>
        </div>

        <div>
          <Typography variant="h3">{t('projects')}</Typography>
          <Typography variant="subtitle1">
            {t('common:choose.the.project.for.which.you.want.to.add.exception.cases')}
          </Typography>
          <Autocomplete
            freeSolo
            filterOptions={(option) => option}
            isOptionEqualToValue={(option, value) => option?.code === value?.code}
            style={{ width: '100%', marginRight: '10px', marginTop: '8px' }}
            size="small"
            defaultValue={selectedCostCenter}
            onChange={(event, newValue) => {
              setSelectedCostCenter(newValue as Project);
            }}
            inputValue={costCenter}
            onInputChange={(event, newInputValue) => {
              setCostCenter(newInputValue);
              if (newInputValue.length >= 1) {
                setSearchCostCenter(newInputValue);
              }
            }}
            options={costCenters?.content || []}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : `${option.code} - ${option.name} (${option.branchName})`
            }
            renderOption={(params, option) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Box component="li" sx={{ fontSize: '13px', '& > img': { mr: 2, flexShrink: 0 } }} {...params}>
                {`${option.code} - ${option.name} (${option.branchName})`}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                sx={{
                  '& input': {
                    padding: '0px',
                  },
                }}
                size="small"
                style={{ width: 'inherit', height: '32px', fontSize: '13px' }}
                ref={params.InputProps.ref}
                InputProps={
                  {
                    ...params.inputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <img src={searchIcon} alt={t('common:search.icon') as string} />
                      </InputAdornment>
                    ),
                    style: {
                      paddingRight: '10px',
                      paddingTop: '0px',
                      paddingBottom: '0px',
                      paddingLeft: '10px',
                      fontSize: '13px',
                    },
                  } as InputProps
                }
                autoFocus
                placeholder={t('common:enter.result.center.name.or.code') as string}
              />
            )}
          />
        </div>

        <div style={{ marginTop: '24px' }}>
          <Typography variant="h3">{t('common:payer.project')}</Typography>
          <Typography variant="subtitle1">{t('now.enter.the.project.you.want.to.add.as.a.payer')}</Typography>
          <div style={{ display: 'flex', alignItems: 'end' }}>
            <Autocomplete
              freeSolo
              filterOptions={(option) => option}
              isOptionEqualToValue={(option, value) => option?.code === value?.code}
              style={{ width: '100%', marginTop: '8px' }}
              size="small"
              defaultValue={selectedPayingCostCenter}
              onChange={(event, newValue) => {
                setSelectedPayingCostCenter(newValue as Project);
              }}
              inputValue={payingCostCenter}
              onInputChange={(event, newInputValue) => {
                setPayingCostCenter(newInputValue);
                if (newInputValue.length >= 1) {
                  setSearchPayingCostCenter(newInputValue);
                }
              }}
              options={payingCostCenters?.content || []}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.code} - ${option.name} (${option.branchName})`
              }
              renderOption={(params, option) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <Box component="li" sx={{ fontSize: '13px', '& > img': { mr: 2, flexShrink: 0 } }} {...params}>
                  {`${option.code} - ${option.name} (${option.branchName})`}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& input': {
                      padding: '0px',
                    },
                  }}
                  size="small"
                  style={{ width: 'inherit', height: '32px', fontSize: '13px' }}
                  ref={params.InputProps.ref}
                  InputProps={
                    {
                      ...params.inputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <img src={searchIcon} alt={t('common:search.icon') as string} />
                        </InputAdornment>
                      ),
                      style: {
                        paddingRight: '10px',
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        paddingLeft: '10px',
                        fontSize: '13px',
                      },
                    } as InputProps
                  }
                  autoFocus
                  placeholder={t('common:search.by.project.name.or.code') as string}
                />
              )}
            />
          </div>
          <div style={{ width: '100%', border: '1px solid #CCCCCC', marginTop: '32px' }} />
        </div>
        <div style={{ marginTop: '24px' }}>
          <Typography variant="h3">{t('exceptions')}</Typography>
          <ul>
            {copyProject?.exceptions?.map((item) => (
              <li key={item}>
                <Typography>{item}</Typography>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginTop: '16px',
          paddingBottom: '16px',
        }}
      >
        <Button
          variant="secondary"
          onClick={() => {
            onClose();
          }}
        >
          {t('common:cancel')}
        </Button>

        <Button
          variant="primary"
          disabled={!selectedCostCenter || !selectedPayingCostCenter}
          onClick={() => {
            setConfirmDialog(true);
          }}
          sx={{ marginLeft: '16px' }}
        >
          {t('common:save')}
        </Button>
      </div>
      <CancelOrConfirmDialog
        open={confirmDialog}
        title={t('copy.exceptions')}
        contentText={t('Would.you.like.to.make.a.copy.of.the.exceptions.for.this.new.registration')}
        onClose={() => {
          setConfirmDialog(false);
        }}
        onConfirm={() => {
          handleSubmit();
          onClose();
        }}
        cancelText={t('common:cancel')}
        confirmText={t('confirm')}
      />
    </div>
  );
}
